import { useEffect, useState } from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    Box,
    Typography,
    Paper,
    Chip,
    alpha,
    InputAdornment
} from '@mui/material';
import { useSelector } from 'react-redux';
import { getGraphs } from '../../../http/complexesApi.js';
import { OrbitProgress } from 'react-loading-indicators';
import Viewer from 'react-viewer';

// Иконки
import BarChartIcon from '@mui/icons-material/BarChart';
import DevicesIcon from '@mui/icons-material/Devices';
import ImageIcon from '@mui/icons-material/Image';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

const ComplexGraphs = props => {
    const { host, hostType = 'default' } = props;
    const isDarkMode = useSelector(state => state.settings.darkMode);
    const [hostid, setHostid] = useState(null);
    const [graphs, setGraphs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewerVisible, setViewerVisible] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const getHosts = () => {
        let hosts = [host];
        if (hostType === 'crossroad' && host.child) {
            hosts = [...hosts, ...host.child];
        }
        return hosts;
    };

    const getHostDisplayName = (h) => {
        if (hostType === 'crossroad') {
            return h.name.split('/')[1];
        }
        return h.name;
    };

    const hosts = getHosts();

    useEffect(() => {
        if (hosts.length > 0 && !hostid) {
            setHostid(hosts[0].hostid);
        }
    }, [hosts, hostid]);

    useEffect(() => {
        if (!hostid) return;
        setLoading(true);

        const fetchGraphs = async (id) => {
            try {
                const res = await getGraphs(id);
                setGraphs(res);
            } catch (err) {
                console.error('Error fetching graphs:', err);
                setTimeout(() => setHostid(id), 15 * 1000);
            }
        };

        fetchGraphs(hostid);
    }, [hostid]);

    useEffect(() => {
        if (graphs && graphs.graphs && graphs.graphs.length > 0) {
            setLoading(false);
        }
    }, [graphs]);

    const renderGraph = (graph, index) => {
        const imageData = `data:image/jpeg;base64,${graph[1]}`;
        const graphName = graph[0] || `График ${index + 1}`;

        return (
            <Paper
                key={graph[0]}
                elevation={0}
                sx={{
                    borderRadius: { xs: '8px', md: '12px' },
                    backgroundColor: 'var(--background-paper)',
                    border: '1px solid var(--border-default)',
                    overflow: 'hidden',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        boxShadow: isDarkMode ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.1)',
                    }
                }}
            >
                {/* Заголовок графика */}
                <Box sx={{
                    p: { xs: 0.75, md: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.75, md: 1 },
                    backgroundColor: 'var(--background-subtle)',
                    borderBottom: '1px solid var(--border-default)'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 20, md: 24 },
                        height: { xs: 20, md: 24 },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        flexShrink: 0
                    }}>
                        <BarChartIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
                    </Box>
                    <Typography sx={{ 
                        fontSize: { xs: '0.75rem', md: '0.8rem' }, 
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {graphName}
                    </Typography>
                </Box>

                {/* Изображение */}
                <Box
                    onClick={() => {
                        setCurrentImageIndex(index);
                        setViewerVisible(true);
                    }}
                    sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover .zoom-overlay': { opacity: 1 }
                    }}
                >
                    <img
                        src={imageData}
                        width="100%"
                        alt={graphName}
                        style={{ display: 'block' }}
                    />
                    <Box
                        className="zoom-overlay"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.4, md: 0.5 },
                            px: { xs: 1, md: 1.5 },
                            py: { xs: 0.5, md: 0.75 },
                            borderRadius: { xs: '6px', md: '8px' },
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            color: '#000'
                        }}>
                            <ZoomInIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                            <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, fontWeight: 500 }}>
                                Увеличить
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        );
    };

    const getViewerImages = () => {
        if (!graphs || !graphs.graphs) return [];
        return graphs.graphs.map((graph, index) => ({
            src: `data:image/jpeg;base64,${graph[1]}`,
            alt: graph[0] || `График ${index + 1}`
        }));
    };

    const selectedHost = hosts.find(h => h.hostid === hostid);

    return (
        <Box sx={{ 
            p: { xs: 1, md: 1.5 }, 
            width: '100%', 
            height: '100%', 
            overflow: 'auto',
            backgroundColor: 'var(--background-default)',
            '&::-webkit-scrollbar': { width: { xs: '4px', md: '6px' } },
            '&::-webkit-scrollbar-track': { background: 'var(--background-subtle)', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': { 
                background: 'var(--border-default)', 
                borderRadius: '3px',
                '&:hover': { background: 'var(--text-muted)' }
            }
        }}>
            {/* Панель выбора устройства */}
            {hosts.length > 1 && (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: { xs: 1, md: 1.5 }, 
                    mb: { xs: 1, md: 1.5 },
                    p: { xs: 0.75, md: 1 },
                    backgroundColor: 'var(--background-paper)',
                    borderRadius: { xs: '8px', md: '10px' },
                    border: '1px solid var(--border-default)'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 28, md: 32 },
                        height: { xs: 28, md: 32 },
                        borderRadius: { xs: '6px', md: '8px' },
                        backgroundColor: 'var(--secondary-color)',
                        color: 'white',
                        flexShrink: 0
                    }}>
                        <DevicesIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, color: 'var(--text-muted)', mb: 0.25 }}>
                            Устройство
                        </Typography>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={hostid || ''}
                                onChange={(e) => setHostid(e.target.value)}
                                displayEmpty
                                sx={{
                                    backgroundColor: isDarkMode ? 'var(--background-subtle)' : 'var(--background-default)',
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '0.8rem', md: '0.85rem' },
                                    height: { xs: 32, md: 36 },
                                    borderRadius: { xs: '6px', md: '8px' },
                                    '& .MuiOutlinedInput-notchedOutline': { 
                                        borderColor: 'var(--border-default)' 
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                                        borderColor: 'var(--primary-color)' 
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                                        borderColor: 'var(--primary-color)' 
                                    },
                                    '& .MuiSelect-icon': { 
                                        color: 'var(--text-secondary)' 
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            backgroundColor: 'var(--background-paper)',
                                            backgroundImage: 'none',
                                            border: '1px solid var(--border-default)',
                                            boxShadow: 'var(--shadow-lg)',
                                            borderRadius: { xs: '6px', md: '8px' },
                                            '& .MuiMenuItem-root': {
                                                color: 'var(--text-primary)',
                                                fontSize: { xs: '0.8rem', md: '0.85rem' },
                                                minHeight: { xs: 32, md: 36 },
                                                '&:hover': { backgroundColor: 'var(--hover-bg)' },
                                                '&.Mui-selected': {
                                                    backgroundColor: 'var(--primary-color)',
                                                    color: 'white',
                                                    '&:hover': { backgroundColor: 'var(--primary-dark)' }
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                {hosts.map(h => (
                                    <MenuItem key={h.hostid} value={h.hostid}>
                                        {getHostDisplayName(h)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Chip
                        label={`${hosts.length} устр.`}
                        size="small"
                        sx={{
                            height: { xs: 20, md: 24 },
                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                            fontWeight: 600,
                            backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                            color: 'var(--primary-color)',
                            border: `1px solid ${alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.3)}`,
                            flexShrink: 0
                        }}
                    />
                </Box>
            )}

            {/* Загрузка */}
            {loading ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: { xs: 150, md: 200 },
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid var(--border-default)'
                    }}
                >
                    <OrbitProgress color={isDarkMode ? "#58a6ff" : "#0969da"} size="medium" text="" textColor="" />
                    <Typography sx={{ color: 'var(--text-muted)', mt: { xs: 1.5, md: 2 }, fontSize: { xs: '0.8rem', md: '0.85rem' } }}>
                        Загрузка графиков...
                    </Typography>
                </Paper>
            ) : (
                <>
                    {graphs && graphs.graphs && graphs.graphs.length > 0 ? (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                            gap: { xs: 1, md: 1.5 }
                        }}>
                            {graphs.graphs.map((graph, index) => renderGraph(graph, index))}
                        </Box>
                    ) : (
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, md: 4 },
                                textAlign: 'center',
                                backgroundColor: 'var(--background-paper)',
                                borderRadius: { xs: '8px', md: '12px' },
                                border: '1px solid var(--border-default)'
                            }}
                        >
                            <ImageIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--text-muted)', mb: { xs: 1.5, md: 2 }, opacity: 0.5 }} />
                            <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                                Графики не найдены
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                                Для выбранного устройства графики отсутствуют
                            </Typography>
                        </Paper>
                    )}
                </>
            )}

            {/* Viewer */}
            <Viewer
                zIndex={9999}
                visible={viewerVisible}
                onClose={() => setViewerVisible(false)}
                images={getViewerImages()}
                activeIndex={currentImageIndex}
                onChange={(activeImage, activeIndex) => setCurrentImageIndex(activeIndex)}
            />
        </Box>
    );
};

export default ComplexGraphs;
